import { useCallback, useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { KimThread } from '../../../../../../../../../../backend/src/matter/subset/thread/kim-thread.entity';
import { Icon } from '@/new/components/icon';
import { SearchInput } from '@/new/components/search-input';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/new/components/shadcn/dialog';
import { cn } from '@/new/lib/utils';

export function PublishedThreadsDialog({
  publishedThreads,
  collapsed,
}: {
  publishedThreads: KimThread[];
  collapsed: boolean;
}) {
  const { matterId, subsetId } = useParams();
  const { i18n } = useTranslation();

  const [publishedSearchQuery, setPublishedSearchQuery] = useState('');
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(true);
  const scrollElRef = useRef<HTMLDivElement | null>(null);

  const handleScroll = useCallback(() => {
    const el = scrollElRef.current;
    if (!el) return;
    setIsScrolledToBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 5);
  }, []);

  const scrollRef = useCallback(
    (node: HTMLDivElement | null) => {
      scrollElRef.current = node;
      handleScroll();
    },
    [handleScroll],
  );

  const filteredThreads = publishedThreads?.filter((t) =>
    t.name.toLowerCase().includes(publishedSearchQuery.toLowerCase()),
  );

  useEffect(() => {
    handleScroll();
  }, [publishedThreads?.length, handleScroll]);

  return (
    <Dialog>
      <DialogTrigger
        className={cn(
          'mx-2 flex items-center justify-between gap-2 overflow-hidden rounded-md px-2 py-1.25 text-xs text-gray-600 ring-offset-2 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus-visible:ring focus-visible:ring-teal-500',
        )}
      >
        <div className="flex items-center gap-2">
          <Icon className="lucide--users-round text-sm duration-150" />
          <div className={cn(collapsed ? 'opacity-0' : 'opacity-100', 'line-clamp-1 text-left break-all duration-150')}>
            Published Threads
          </div>
        </div>
      </DialogTrigger>

      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            <Icon className="lucide--users-round" /> Published Threads
          </DialogTitle>
          <DialogDescription>View threads published by other users in this Matter.</DialogDescription>
        </DialogHeader>

        <div className="-m-2 -mx-3">
          <SearchInput
            placeholder={`Search ${filteredThreads?.length ?? 0} published ${filteredThreads?.length === 1 ? 'thread' : 'threads'}...`}
            value={publishedSearchQuery}
            onChange={(e) => setPublishedSearchQuery(e.target.value)}
          />

          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="flex h-[50vh] flex-col overflow-y-auto mask-b-from-65% mask-b-to-100% px-1 pt-1.5 transition-[mask-size] duration-200"
            style={{ maskSize: isScrolledToBottom ? '100% 135%' : '100% 100%' }}
          >
            {filteredThreads?.length === 0 && (
              <p className="my-12 text-center text-gray-400">
                {publishedSearchQuery ? 'No matching threads.' : 'No published threads yet.'}
              </p>
            )}

            {filteredThreads?.map((thread) => (
              <DialogClose asChild key={thread.id}>
                <Link
                  to={`/matters/${matterId}/subsets/${subsetId}/new-kim/${thread.id}`}
                  className="group focus:outline-none"
                >
                  <div className="space-y-1 rounded-md p-3 ring-teal-500 ring-offset-2 transition-colors duration-150 group-focus-visible:ring-1 hover:bg-gray-50">
                    <p className="truncate font-medium">{thread.name}</p>
                    <div className="text-xs text-gray-400">
                      <span>
                        {new Date(thread.createdDate).toLocaleString(i18n.language, {
                          dateStyle: 'medium',
                          timeStyle: 'medium',
                        })}
                      </span>
                      <span className="mx-1">&middot;</span>
                      <span>{thread.createdBy.email}</span>
                    </div>
                  </div>
                  <hr className="mx-1 my-px group-last:opacity-0" />
                </Link>
              </DialogClose>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
